import React, { FC } from 'react';
import { TextInput, useTranslate, BooleanInput, NumberInput, SelectArrayInput } from 'react-admin';
import { l } from '~/utils/solutions';
import { useStyles } from '~/hooks/useSolutionsStyles';
import { Configuration } from '~/api/solutions';
import { JsonInput } from './JsonInput';
import { CodeForm } from './CodeForm';
import { H3 } from '~/components';
import { Solution } from '~/types';
import { useGetSolutionLegalConsents, useGetSolutionLegalConsentsByIds } from '~/hooks/solutions';
import { LegalConsentConfiguration } from '~/types/LegalConsent';

interface ProfileFormProps {
  keyName: string;
  record?: Solution;
  configurationRecord?: Configuration;
}

export const ProfileForm: FC<ProfileFormProps> = ({ keyName, record, configurationRecord }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(`featuresConfig.${key}`));
  const td = (key: string): string => translate(`utils.${key}`);
  const isIndividualProfile = keyName === 'veengu_individual_profile';

  const { data: consents } = useGetSolutionLegalConsents(record?.id, isIndividualProfile);
  const selectedConsentIds: string[] = isIndividualProfile
    ? (configurationRecord?.[keyName]?.legalConsents as string[] | undefined) ?? []
    : [];
  const consentOptions = isIndividualProfile
    ? (consents ?? []).map((c: LegalConsentConfiguration) => ({
        id: c.id,
        name: `${c.type} (${c.version})`,
      }))
    : [];
  const consentOptionIds = isIndividualProfile
    ? new Set(consentOptions.map((option) => option.id))
    : new Set<string>();
  const archivedConsentIds = isIndividualProfile
    ? selectedConsentIds.filter((id) => !consentOptionIds.has(id))
    : [];
  const archivedConsentQueries = useGetSolutionLegalConsentsByIds(
    record?.id,
    archivedConsentIds,
    isIndividualProfile
  );
  const archivedConsentNameById = new Map<string, string>(
    archivedConsentQueries
      .map((query) => query.data)
      .filter((consent): consent is LegalConsentConfiguration => !!consent)
      .map((consent) => [consent.id, `${consent.type} (${consent.version})`])
  );
  const archivedSelectedOptions = archivedConsentIds.map((id) => {
    const name = archivedConsentNameById.get(id);
    return { id, name: name ? `${name} (archived)` : `${id} (archived)` };
  });
  const availableConsentOptions = isIndividualProfile
    ? [...consentOptions, ...archivedSelectedOptions]
    : [];

  return (
    <>
      <JsonInput
        label={t('profileSchema')}
        source={`${keyName}.profileSchema`}
        className={classes.schemaInput}
        validationError={t('jsonError')}
      />
      {isIndividualProfile && record?.id && (
        <SelectArrayInput
          source={`${keyName}.legalConsents`}
          label={t('legalConsents')}
          choices={availableConsentOptions}
          allowEmpty
          translateChoice={false}
        />
      )}
      {isIndividualProfile && (
        <JsonInput
          label={t('nonAccountBasedPaymentInstruments')}
          source={`${keyName}.nonAccountBasedPaymentInstruments`}
          className={classes.schemaInput}
          validationError={t('jsonError')}
          validationEmptyError={td('jsonEmptyArrayObjectError')}
          initialValue='[]'
          defaultValue='[]'
        />
      )}
      {isIndividualProfile && <CodeForm getSource={(field) => `${keyName}.${field}`} />}
      {keyName === 'veengu_business_profile' && (
        <>
          <JsonInput
            label={t('employeeConfig')}
            source={`${keyName}.employeeConfig`}
            className={classes.schemaInput}
            validationError={t('jsonError')}
          />
          <TextInput
            label={t('businessPortalBaseUrl')}
            source={`${keyName}.businessPortalBaseUrl`}
          />
          <BooleanInput
            label={t('requireMappingToLocation')}
            source={`${keyName}.requireMappingToLocation`}
          />
          <BooleanInput
            label={t('hideAccountBalancesLinkedToTerminal')}
            source={`${keyName}.hideAccountBalancesLinkedToTerminal`}
          />
          <NumberInput
            label={t('terminalActivationCodeExpiration')}
            source={`${keyName}.activationCodeExpirationDurationInSeconds`}
            helperText={t('terminalActivationCodeExpirationHelper')}
            min={1}
            step={1}
            className={classes.inputWidth}
            format={(value?: number) => (value ? Math.floor(value / 3600) : '')}
            parse={(value?: number) => (value ? value * 3600 : undefined)}
          />
        </>
      )}
      <H3>{t('balanceChecks')}</H3>
      <BooleanInput
        label={t('ignoreBalanceChecksOnReversalViaDashboardApi')}
        source={`${keyName}.balanceChecks.ignoreOnReversalViaDashboardApi`}
      />
      <BooleanInput
        label={t('ignoreBalanceChecksOnForcedApprovalViaDashboardApi')}
        source={`${keyName}.balanceChecks.ignoreOnForcedApprovalViaDashboardApi`}
      />
      <BooleanInput
        label={t('ignoreBalanceChecksOnReversalViaIntegrationApi')}
        source={`${keyName}.balanceChecks.ignoreOnReversalViaIntegrationApi`}
      />
    </>
  );
};
